import type {
  Alignment,
  Borders,
  Cell,
  CellValue,
  Fill,
  Font,
  Worksheet,
} from 'exceljs';

export type MonthlyPayrollExportItem = {
  department?: string | null;
  employeeName?: string | null;
  position?: string | null;
  religion?: string | null;
  gender?: string | null;
  ptkpStatus?: string | null;
  basicSalary?: string | number | null;
  basicDay?: string | number | null;
  transportMealAllowance?: string | number | null;
  accommodation?: string | number | null;
  overtime?: string | number | null;
  jht?: string | number | null;
  bpjsKesehatan?: string | number | null;
  advance?: string | number | null;
  absence?: string | number | null;
  pph21?: string | number | null;
  late?: string | number | null;
};

export type MonthlyPayrollExportGroup = {
  department: string;
  items: MonthlyPayrollExportItem[];
};

type CellStyle = {
  font?: Partial<Font>;
  fill?: Fill;
  border?: Partial<Borders>;
  alignment?: Partial<Alignment>;
};

const COLS = [
  'A',
  'B',
  'C',
  'D',
  'E',
  'F',
  'G',
  'H',
  'I',
  'J',
  'K',
  'L',
  'M',
  'N',
  'O',
  'P',
  'Q',
  'R',
  'S',
  'T',
] as const;

const COLUMN_WIDTHS = [
  4.11, 31.84, 27.43, 10.27, 7.42, 6.46, 12.18, 11.15, 11.26, 11.15, 12.47,
  13.05, 14.96, 10.71, 10.27, 11.15, 12.18, 10.27, 10.85, 12.18,
];

const GRAY = 'FF999999';
const THIN = {style: 'thin' as const, color: {argb: 'FF000000'}};
const ALL_BORDER: Partial<Borders> = {
  top: THIN,
  left: THIN,
  bottom: THIN,
  right: THIN,
};
const HEADER_FILL: Fill = {
  type: 'pattern',
  pattern: 'solid',
  fgColor: {argb: GRAY},
};
const HEADER_FONT: Partial<Font> = {
  name: 'Times New Roman',
  size: 10,
  bold: true,
  color: {argb: 'FF000000'},
};
const DATA_FONT: Partial<Font> = {
  name: 'Times New Roman',
  size: 10,
  color: {argb: 'FF000000'},
};
const MONEY_FORMAT = '#,##0.00';

const CENTER: Partial<Alignment> = {
  vertical: 'middle',
  horizontal: 'center',
  wrapText: true,
};
const LEFT: Partial<Alignment> = {
  vertical: 'middle',
  horizontal: 'left',
  wrapText: false,
};
const RIGHT: Partial<Alignment> = {
  vertical: 'middle',
  horizontal: 'right',
  wrapText: false,
};

const OPTIONAL_ZERO_KEYS = new Set(['advance', 'absence', 'late']);

export const toAmount = (value: string | number | null | undefined): number => {
  if (value === null || value === undefined || value === '') {
    return 0;
  }
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : 0;
  }
  const numeric = Number(String(value).replace(/,/g, '').trim());
  return Number.isNaN(numeric) ? 0 : numeric;
};

export const departmentLabel = (name: string | null | undefined): string => {
  const trimmed = String(name ?? '').trim();
  return (trimmed === '' ? 'NO SUB UNIT' : trimmed).toUpperCase();
};

export const groupItemsByDepartment = (
  items: MonthlyPayrollExportItem[] | null | undefined,
): MonthlyPayrollExportGroup[] => {
  const groups: MonthlyPayrollExportGroup[] = [];
  for (const item of items ?? []) {
    const key = departmentLabel(item.department);
    const last = groups[groups.length - 1];
    if (last && last.department === key) {
      last.items.push(item);
    } else {
      groups.push({department: key, items: [item]});
    }
  }
  return groups;
};

const applyRange = (
  worksheet: Worksheet,
  range: string,
  style: CellStyle,
): void => {
  const [start, end] = range.split(':');
  const startRef = start ?? '';
  const endRef = end ?? startRef;
  const startCol = startRef.replace(/[0-9]/g, '');
  const startRow = Number(startRef.replace(/[A-Z]/g, ''));
  const endCol = endRef.replace(/[0-9]/g, '');
  const endRow = Number(endRef.replace(/[A-Z]/g, ''));
  const startIndex = COLS.indexOf(startCol as (typeof COLS)[number]);
  const endIndex = COLS.indexOf(endCol as (typeof COLS)[number]);
  for (let row = startRow; row <= endRow; row++) {
    for (let i = startIndex; i <= endIndex; i++) {
      Object.assign(worksheet.getCell(`${COLS[i]}${row}`), style);
    }
  }
};

const setCell = (
  worksheet: Worksheet,
  address: string,
  value: CellValue,
  extra: CellStyle = {},
): Cell => {
  const cell = worksheet.getCell(address);
  cell.value = value;
  Object.assign(cell, extra);
  return cell;
};

const writeHeader = (worksheet: Worksheet): void => {
  worksheet.columns = COLUMN_WIDTHS.map((width) => ({width}));

  const titles: Array<string | null> = [
    'No.',
    'Name',
    'Position',
    'Religion',
    'Gender',
    'PTKP',
    'Basic Salary',
    'basic/day',
    'Transp. & Meal Allowance',
    'Accomodation',
    'OVER TIME',
    'DEDUCTION',
    null,
    null,
    null,
    'Total',
    'Total ',
    'PPh 21',
    'Late',
    'Total',
  ];
  titles.forEach((title, index) => {
    if (title !== null) {
      worksheet.getCell(`${COLS[index]}1`).value = title;
    }
  });
  worksheet.getCell('L2').value = 'JHT 3%';
  worksheet.getCell('M2').value = 'BPJS Kesehatan';
  worksheet.getCell('N2').value = 'ADVANCE';
  worksheet.getCell('O2').value = 'Absence';
  worksheet.getCell('P2').value = 'Deduction';
  worksheet.getCell('Q2').value = '(BC+A-D)';

  [
    'A1:A2',
    'B1:B2',
    'C1:C2',
    'D1:D2',
    'E1:E2',
    'F1:F2',
    'G1:G2',
    'H1:H2',
    'I1:I2',
    'J1:J2',
    'K1:K2',
    'L1:O1',
    'R1:R2',
    'S1:S2',
    'T1:T2',
  ].forEach((range) => worksheet.mergeCells(range));

  applyRange(worksheet, 'A1:T2', {
    font: HEADER_FONT,
    fill: HEADER_FILL,
    border: ALL_BORDER,
    alignment: CENTER,
  });

  for (let i = 0; i < 20; i++) {
    const value = i === 18 ? null : i < 18 ? i + 1 : 19;
    setCell(worksheet, `${COLS[i]}3`, value, {
      font: DATA_FONT,
      border: ALL_BORDER,
      alignment: CENTER,
    });
  }

  worksheet.getRow(1).height = 18;
  worksheet.getRow(2).height = 18;
  worksheet.getRow(3).height = 15;
};

const writeDepartmentRow = (
  worksheet: Worksheet,
  rowNumber: number,
  name: string,
): void => {
  applyRange(worksheet, `A${rowNumber}:T${rowNumber}`, {
    font: HEADER_FONT,
    fill: HEADER_FILL,
    border: ALL_BORDER,
    alignment: CENTER,
  });
  worksheet.getCell(`A${rowNumber}`).value = 0;
  worksheet.getCell(`B${rowNumber}`).value = name;
  worksheet.getCell(`B${rowNumber}`).alignment = LEFT;
  worksheet.getCell(`T${rowNumber}`).value = 0;
  worksheet.getCell(`T${rowNumber}`).numFmt = MONEY_FORMAT;
};

const writeAmount = (
  worksheet: Worksheet,
  address: string,
  amount: number,
  {optionalZero = false}: {optionalZero?: boolean} = {},
): void => {
  const cell = worksheet.getCell(address);
  cell.font = DATA_FONT;
  cell.border = ALL_BORDER;
  cell.alignment = RIGHT;
  cell.numFmt = MONEY_FORMAT;
  if (optionalZero && amount === 0) {
    cell.value = null;
    return;
  }
  cell.value = amount;
};

const writeEmployeeRow = (
  worksheet: Worksheet,
  rowNumber: number,
  seq: number,
  item: MonthlyPayrollExportItem,
): void => {
  applyRange(worksheet, `A${rowNumber}:T${rowNumber}`, {
    font: DATA_FONT,
    border: ALL_BORDER,
    alignment: CENTER,
  });

  worksheet.getCell(`A${rowNumber}`).value = seq;
  worksheet.getCell(`B${rowNumber}`).value = item.employeeName ?? '';
  worksheet.getCell(`B${rowNumber}`).alignment = LEFT;
  worksheet.getCell(`C${rowNumber}`).value = item.position ?? '';
  worksheet.getCell(`C${rowNumber}`).alignment = LEFT;
  worksheet.getCell(`D${rowNumber}`).value = item.religion ?? '';
  worksheet.getCell(`E${rowNumber}`).value = item.gender ?? '';
  worksheet.getCell(`F${rowNumber}`).value = item.ptkpStatus ?? '';

  const amounts: Record<string, number> = {
    G: toAmount(item.basicSalary),
    H: toAmount(item.basicDay),
    I: toAmount(item.transportMealAllowance),
    J: toAmount(item.accommodation),
    K: toAmount(item.overtime),
    L: toAmount(item.jht),
    M: toAmount(item.bpjsKesehatan),
    N: toAmount(item.advance),
    O: toAmount(item.absence),
    R: toAmount(item.pph21),
    S: toAmount(item.late),
  };
  Object.entries(amounts).forEach(([col, amount]) => {
    const key =
      col === 'N'
        ? 'advance'
        : col === 'O'
        ? 'absence'
        : col === 'S'
        ? 'late'
        : '';
    writeAmount(worksheet, `${col}${rowNumber}`, amount, {
      optionalZero: OPTIONAL_ZERO_KEYS.has(key),
    });
  });

  worksheet.getCell(`P${rowNumber}`).value = {
    formula: `N(L${rowNumber})+N(M${rowNumber})+N(N${rowNumber})+N(O${rowNumber})`,
  };
  worksheet.getCell(`Q${rowNumber}`).value = {
    formula: `N(G${rowNumber})+N(I${rowNumber})+N(J${rowNumber})+N(K${rowNumber})-N(P${rowNumber})`,
  };
  worksheet.getCell(`T${rowNumber}`).value = {
    formula: `N(Q${rowNumber})-N(R${rowNumber})-N(S${rowNumber})`,
  };
  ['P', 'Q', 'T'].forEach((col) => {
    const cell = worksheet.getCell(`${col}${rowNumber}`);
    cell.font = DATA_FONT;
    cell.border = ALL_BORDER;
    cell.alignment = RIGHT;
    cell.numFmt = MONEY_FORMAT;
  });
};

const writeTotalRow = (
  worksheet: Worksheet,
  rowNumber: number,
  firstDataRow: number,
  lastDataRow: number,
): void => {
  applyRange(worksheet, `A${rowNumber}:T${rowNumber}`, {
    font: HEADER_FONT,
    fill: HEADER_FILL,
    border: ALL_BORDER,
    alignment: CENTER,
  });
  worksheet.getCell(`A${rowNumber}`).value = 0;
  worksheet.getCell(`B${rowNumber}`).value = 'TOTAL';
  worksheet.getCell(`B${rowNumber}`).alignment = LEFT;

  const dataRange = lastDataRow >= firstDataRow;
  [
    'G',
    'H',
    'I',
    'J',
    'K',
    'L',
    'M',
    'N',
    'O',
    'P',
    'Q',
    'R',
    'S',
    'T',
  ].forEach((col) => {
    const cell = worksheet.getCell(`${col}${rowNumber}`);
    cell.numFmt = MONEY_FORMAT;
    cell.alignment = RIGHT;
    if (dataRange) {
      cell.value = {
        formula: `SUMIF($A$${firstDataRow}:$A$${lastDataRow},">0",${col}${firstDataRow}:${col}${lastDataRow})`,
      };
    } else {
      cell.value = 0;
    }
  });
};

export const buildMonthlyPayrollSheet = (
  worksheet: Worksheet,
  items: MonthlyPayrollExportItem[] = [],
): void => {
  worksheet.name = 'Sheet1';
  worksheet.properties.defaultRowHeight = 15;
  worksheet.views = [{state: 'normal', showGridLines: true, zoomScale: 100}];
  worksheet.pageSetup = {
    orientation: 'landscape',
    fitToPage: true,
    fitToWidth: 1,
    fitToHeight: 1,
    paperSize: 1,
  };

  writeHeader(worksheet);

  const groups = groupItemsByDepartment(items);
  let excelRow = 4;
  let seq = 1;
  const firstDataRow = 4;

  groups.forEach((group) => {
    writeDepartmentRow(worksheet, excelRow, group.department);
    excelRow += 1;
    group.items.forEach((item) => {
      writeEmployeeRow(worksheet, excelRow, seq, item);
      seq += 1;
      excelRow += 1;
    });
  });

  const lastDataRow = excelRow - 1;
  writeTotalRow(
    worksheet,
    excelRow,
    groups.length ? firstDataRow : excelRow,
    lastDataRow,
  );
};
